import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Slider,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "../utils/api";
import { formatUkDate } from "../utils/dateLocale";
import { useAuth } from "../contexts/AuthContext";
import { insertQuillPlaceholder } from "../utils/quillPlaceholder";

const NotificationPreferences = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stats, setStats] = useState(null);
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    notificationEmail: "",
    firstReminderDays: 30,
    secondReminderDays: 7,
    dailyReminderAfterExpiry: true,
    maxDailyReminders: 30,
    globalNotificationsEnabled: true,
  });
  const [emails, setEmails] = useState([]);
  const [globalEmails, setGlobalEmails] = useState([]);
  const [complianceCcEmail, setComplianceCcEmail] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailListType, setEmailListType] = useState("personal"); // personal | global
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // Attestation configuration state
  const [attestationConfig, setAttestationConfig] = useState({
    scheduleType: "quarterly",
    frequencyMonths: 3,
    enabled: false,
    autoCreateAttestations: true,
    testMode: false,
    testUserId: null,
    attestationFirstReminderDays: 14,
    attestationSecondReminderDays: 3,
  });
  const [attestationConfigLoading, setAttestationConfigLoading] =
    useState(false);
  const [triggeringAttestations, setTriggeringAttestations] = useState(false);
  const [testUsers, setTestUsers] = useState([]);
  const [loadingTestUsers, setLoadingTestUsers] = useState(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    templateName: "",
    templateType: "quarterly_review",
    subject: "",
    bodyHtml: "",
  });
  const [templateErrors, setTemplateErrors] = useState({});
  const quillRef = useRef(null);

  useEffect(() => {
    fetchPreferences();
    fetchStats();
    fetchAttestationConfig();
    fetchEmailTemplates();
    fetchTestUsers();
    if (isAdmin) {
      fetchGlobalRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchGlobalRecipients = async () => {
    try {
      const response = await api.get("/api/notification-preferences/global");
      const emailList = response.data.emails || [];
      setGlobalEmails(
        emailList.map((email) => ({
          ...email,
          is_primary: Boolean(email.is_primary),
          is_active: Boolean(email.is_active),
        })),
      );
      setComplianceCcEmail(response.data.settings?.compliance_cc_email || "");
    } catch (err) {
      console.error("Failed to fetch global recipients:", err);
    }
  };

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/notification-preferences");
      const prefs = response.data.preferences;
      const emailList = response.data.emails || [];

      setPreferences({
        emailNotifications: Boolean(prefs.email_notifications),
        notificationEmail: prefs.notification_email || "",
        firstReminderDays: prefs.first_reminder_days,
        secondReminderDays: prefs.second_reminder_days,
        dailyReminderAfterExpiry: Boolean(prefs.daily_reminder_after_expiry),
        maxDailyReminders: prefs.max_daily_reminders,
        globalNotificationsEnabled: Boolean(prefs.global_notifications_enabled),
      });

      // Convert numeric boolean values to actual booleans
      const processedEmails = emailList.map((email) => ({
        ...email,
        is_primary: Boolean(email.is_primary),
        is_active: Boolean(email.is_active),
      }));

      setEmails(processedEmails);
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
      setError("Failed to load notification preferences");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get("/api/notification-preferences/stats");
      setStats(response.data.stats);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Validate that second reminder is before first reminder
      if (preferences.secondReminderDays >= preferences.firstReminderDays) {
        setError(
          "Second reminder must be sent before the first reminder (use fewer days)",
        );
        return;
      }

      const trimmedCc = complianceCcEmail.trim();
      if (
        isAdmin &&
        trimmedCc &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedCc)
      ) {
        setError("Compliance CC must be a valid email address");
        return;
      }

      await api.put("/api/notification-preferences", preferences);

      if (isAdmin) {
        await api.put("/api/notification-preferences/global/settings", {
          enabled: preferences.globalNotificationsEnabled,
          emailNotifications: preferences.emailNotifications,
          firstReminderDays: preferences.firstReminderDays,
          secondReminderDays: preferences.secondReminderDays,
          dailyReminderAfterExpiry: preferences.dailyReminderAfterExpiry,
          maxDailyReminders: preferences.maxDailyReminders,
          complianceCcEmail: trimmedCc || null,
        });
      }

      setSuccess("Notification preferences updated successfully!");

      // Refresh stats after update
      fetchStats();
      if (isAdmin) {
        fetchGlobalRecipients();
      }
    } catch (error) {
      console.error("Failed to update preferences:", error);
      setError(
        error.response?.data?.error ||
          "Failed to update notification preferences",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setPreferences((prev) => ({ ...prev, [field]: value }));
    setError("");
    setSuccess("");
  };

  const handleSliderChange = (field) => (event, newValue) => {
    handleChange(field, newValue);
  };

  // Email management functions
  const handleAddEmail = (listType = "personal") => {
    setEmailListType(listType);
    setEditingEmail(null);
    setNewEmail("");
    setEmailError("");
    setEmailDialogOpen(true);
  };

  const handleEditEmail = (email, listType = "personal") => {
    setEmailListType(listType);
    setEditingEmail(email);
    setNewEmail(email.email);
    setEmailError("");
    setEmailDialogOpen(true);
  };

  const handleDeleteEmail = async (emailId, listType = "personal") => {
    try {
      const path =
        listType === "global"
          ? `/api/notification-preferences/global/emails/${emailId}`
          : `/api/notification-preferences/emails/${emailId}`;
      await api.delete(path);
      setSuccess("Email deleted successfully!");
      if (listType === "global") fetchGlobalRecipients();
      else fetchPreferences();
    } catch (error) {
      console.error("Failed to delete email:", error);
      setError(error.response?.data?.error || "Failed to delete email");
    }
  };

  const handleTogglePrimary = async (emailId, listType = "personal") => {
    try {
      const path =
        listType === "global"
          ? `/api/notification-preferences/global/emails/${emailId}`
          : `/api/notification-preferences/emails/${emailId}`;
      await api.put(path, { isPrimary: true });
      setSuccess("Primary email updated!");
      if (listType === "global") fetchGlobalRecipients();
      else fetchPreferences();
    } catch (error) {
      console.error("Failed to update primary email:", error);
      setError(error.response?.data?.error || "Failed to update primary email");
    }
  };

  const handleToggleActive = async (
    emailId,
    currentActive,
    listType = "personal",
  ) => {
    try {
      const path =
        listType === "global"
          ? `/api/notification-preferences/global/emails/${emailId}`
          : `/api/notification-preferences/emails/${emailId}`;
      await api.put(path, { isActive: !currentActive });
      setSuccess("Email status updated!");
      if (listType === "global") fetchGlobalRecipients();
      else fetchPreferences();
    } catch (error) {
      console.error("Failed to update email status:", error);
      setError(error.response?.data?.error || "Failed to update email status");
    }
  };

  const handleSaveEmail = async () => {
    try {
      setEmailError("");

      if (!newEmail.trim()) {
        setEmailError("Email address is required");
        return;
      }

      const base =
        emailListType === "global"
          ? "/api/notification-preferences/global/emails"
          : "/api/notification-preferences/emails";

      if (editingEmail) {
        await api.put(`${base}/${editingEmail.id}`, {
          email: newEmail.trim(),
        });
        setSuccess("Email updated successfully!");
      } else {
        await api.post(base, {
          email: newEmail.trim(),
        });
        setSuccess("Email added successfully!");
      }

      setEmailDialogOpen(false);
      if (emailListType === "global") fetchGlobalRecipients();
      else fetchPreferences();
    } catch (error) {
      console.error("Failed to save email:", error);
      setEmailError(error.response?.data?.error || "Failed to save email");
    }
  };

  // Attestation configuration functions
  const fetchAttestationConfig = useCallback(async () => {
    try {
      const response = await api.get(
        "/api/attestation-scheduling/schedule-config",
      );
      const config = {
        ...response.data,
        testMode: response.data.testMode || false,
        testUserId: response.data.testUserId || null,
        attestationFirstReminderDays:
          response.data.attestationFirstReminderDays ?? 14,
        attestationSecondReminderDays:
          response.data.attestationSecondReminderDays ?? 3,
      };

      setAttestationConfig(config);

      // If testUserId is set but users haven't loaded yet, fetch them
      if (config.testUserId) {
        // Fetch users separately - don't create circular dependency
        try {
          const usersResponse = await api.get("/api/users");
          const users = Array.isArray(usersResponse.data)
            ? usersResponse.data
            : [];
          setTestUsers(users);

          // If testUserId is set but not in the loaded users, reset it
          if (!users.some((u) => u.id === config.testUserId)) {
            setAttestationConfig((prev) => ({ ...prev, testUserId: null }));
          }
        } catch (error) {
          console.error("Failed to fetch users:", error);
        }
      }
    } catch (error) {
      console.error("Failed to fetch attestation config:", error);
    }
  }, []);

  const fetchTestUsers = useCallback(async () => {
    try {
      setLoadingTestUsers(true);
      const response = await api.get("/api/users");
      // The API returns an array directly, not wrapped in a users property
      const users = Array.isArray(response.data) ? response.data : [];
      setTestUsers(users);

      // If testUserId is set but not in the loaded users, reset it
      setAttestationConfig((prev) => {
        if (prev.testUserId && !users.some((u) => u.id === prev.testUserId)) {
          return { ...prev, testUserId: null };
        }
        return prev;
      });
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setTestUsers([]);
    } finally {
      setLoadingTestUsers(false);
    }
  }, []);

  useEffect(() => {
    if (attestationConfig.testMode) {
      fetchTestUsers();
    }
  }, [attestationConfig.testMode, fetchTestUsers]);

  const handleSaveAttestationConfig = async () => {
    try {
      setAttestationConfigLoading(true);
      setError("");
      await api.put(
        "/api/attestation-scheduling/schedule-config",
        attestationConfig,
      );
      setSuccess("Attestation schedule configuration updated successfully!");
      fetchAttestationConfig();
    } catch (error) {
      console.error("Failed to update attestation config:", error);
      setError(
        error.response?.data?.error ||
          "Failed to update attestation configuration",
      );
    } finally {
      setAttestationConfigLoading(false);
    }
  };

  const handleTriggerAttestations = async () => {
    // Ask if user wants to resend emails for existing attestations
    const resendEmails = window.confirm(
      "Do you want to resend emails for existing attestations?\n\n" +
        "Click OK to resend emails even if attestations already exist for this period.\n" +
        "Click Cancel to only create new attestations (skip existing ones).",
    );

    const confirmMessage = attestationConfig.testMode
      ? `Trigger attestations for test user only? This will send attestation emails to the selected test user.${resendEmails ? "\n\nNote: Will resend emails for existing attestations." : ""}`
      : `Trigger attestations for all primary owners? This will send attestation emails to all users allocated as primary owners.${resendEmails ? "\n\nNote: Will resend emails for existing attestations." : ""}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setTriggeringAttestations(true);
      setError("");
      const response = await api.post(
        "/api/attestation-scheduling/trigger-attestations",
        {
          resendEmails: resendEmails,
        },
      );
      const { created, skipped, emailsSent, emailsFailed, details } =
        response.data;

      let message = `Attestations triggered successfully! `;
      message += `Created: ${created}, Skipped: ${skipped}`;
      if (emailsSent !== undefined) {
        message += `, Emails sent: ${emailsSent}`;
      }
      if (emailsFailed !== undefined && emailsFailed > 0) {
        message += `, Emails failed: ${emailsFailed}`;
      }

      if (details && details.length > 0) {
        console.log("Attestation trigger details:", details);
        // Show warnings if emails failed
        if (emailsFailed > 0) {
          const failedDetails = details.filter(
            (d) => d.includes("Email") || d.includes("template"),
          );
          if (failedDetails.length > 0) {
            message += `. Warnings: ${failedDetails.slice(0, 3).join("; ")}`;
          }
        }
      }

      setSuccess(message);
      // Refresh config to update last sent date
      fetchAttestationConfig();
    } catch (error) {
      console.error("Failed to trigger attestations:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to trigger attestations",
      );
    } finally {
      setTriggeringAttestations(false);
    }
  };

  // Email template functions
  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get(
        "/api/attestation-scheduling/email-templates",
      );
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch email templates:", error);
    }
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      templateName: "",
      templateType: "quarterly_review",
      subject: "",
      bodyHtml: "",
    });
    setTemplateErrors({});
    setTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      templateName: template.template_name,
      templateType: template.template_type,
      subject: template.subject,
      bodyHtml: template.body_html,
    });
    setTemplateErrors({});
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    // Validate form
    const newErrors = {};

    if (!templateForm.templateName.trim()) {
      newErrors.templateName = "Template name is required";
    }

    if (!templateForm.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    // Check if bodyHtml has actual content (not just empty HTML tags)
    const bodyText = templateForm.bodyHtml.replace(/<[^>]*>/g, "").trim();
    if (!bodyText) {
      newErrors.bodyHtml = "Email body is required";
    }

    setTemplateErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setError("");
      setSuccess("");

      if (editingTemplate) {
        await api.put(
          `/api/attestation-scheduling/email-templates/${editingTemplate.id}`,
          templateForm,
        );
        setSuccess("Email template updated successfully!");
      } else {
        await api.post(
          "/api/attestation-scheduling/email-templates",
          templateForm,
        );
        setSuccess("Email template created successfully!");
      }

      setTemplateDialogOpen(false);
      setTemplateErrors({});
      fetchEmailTemplates();
    } catch (error) {
      console.error("Failed to save template:", error);

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach((err) => {
          const fieldMap = {
            templateName: "templateName",
            subject: "subject",
            bodyHtml: "bodyHtml",
            templateType: "templateType",
          };
          const field = fieldMap[err.path] || err.path;
          backendErrors[field] = err.msg;
        });
        setTemplateErrors(backendErrors);
        setError("Please fix the validation errors");
      } else {
        setError(
          error.response?.data?.error || "Failed to save email template",
        );
      }
    }
  };

  const handleSuspendTemplate = async (template) => {
    if (
      !window.confirm(
        template.is_active
          ? `Are you sure you want to suspend "${template.template_name}"? This will prevent it from being used for new attestations.`
          : `Are you sure you want to activate "${template.template_name}"?`,
      )
    ) {
      return;
    }

    try {
      setError("");
      setSuccess("");
      await api.patch(
        `/api/attestation-scheduling/email-templates/${template.id}/status`,
        {
          isActive: !template.is_active,
        },
      );
      setSuccess(
        `Email template ${!template.is_active ? "activated" : "suspended"} successfully!`,
      );
      fetchEmailTemplates();
    } catch (error) {
      console.error("Failed to update template status:", error);
      setError(
        error.response?.data?.error || "Failed to update template status",
      );
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${template.template_name}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setError("");
      setSuccess("");
      await api.delete(
        `/api/attestation-scheduling/email-templates/${template.id}`,
      );
      setSuccess("Email template deleted successfully!");
      fetchEmailTemplates();
    } catch (error) {
      console.error("Failed to delete template:", error);
      setError(error.response?.data?.error || "Failed to delete template");
    }
  };

  const availablePlaceholders = [
    {
      placeholder: "{{recipient_name}}",
      description: "Name of the person receiving the email",
    },
    {
      placeholder: "{{recipient_email}}",
      description: "Email address of the recipient",
    },
    { placeholder: "{{asset_name}}", description: "Name of the asset" },
    { placeholder: "{{asset_tag}}", description: "Asset tag number" },
    { placeholder: "{{device_type}}", description: "Type of device" },
    { placeholder: "{{serial_number}}", description: "Serial number" },
    {
      placeholder: "{{review_period}}",
      description: "Review period (e.g., 2024 Q1)",
    },
    { placeholder: "{{review_deadline}}", description: "Deadline for review" },
    {
      placeholder: "{{confirmation_link}}",
      description: "Link to confirm attestation",
    },
    { placeholder: "{{asset_list}}", description: "List of assets to review" },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Licenses
                </Typography>
                <Typography variant="h4">{stats.total_licenses}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expired Licenses
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.expired_licenses}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expired Certs &amp; Secrets
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.expired_certs ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expiring Soon
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.expiring_soon}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Licenses: {stats.expiring_soon_licenses ?? 0} · Certs:{" "}
                  {stats.expiring_soon_certs ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Global Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Global Settings</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.globalNotificationsEnabled}
                    onChange={(e) =>
                      handleChange(
                        "globalNotificationsEnabled",
                        e.target.checked,
                      )
                    }
                    color="primary"
                  />
                }
                label="Enable All Notifications"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Master toggle to enable or disable all notification alerts
                across all your licenses.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications}
                    onChange={(e) =>
                      handleChange("emailNotifications", e.target.checked)
                    }
                    color="primary"
                    disabled={!preferences.globalNotificationsEnabled}
                  />
                }
                label="Email Notifications"
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="body2" color="textSecondary">
                    License reminder emails (your licenses)
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddEmail("personal")}
                    disabled={
                      !preferences.emailNotifications ||
                      !preferences.globalNotificationsEnabled
                    }
                  >
                    Add Email
                  </Button>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={1}
                >
                  Used for software license expiry reminders tied to licenses
                  you own.
                </Typography>

                {emails.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No email addresses configured. Add an email to receive
                    notifications.
                  </Typography>
                ) : (
                  <List dense>
                    {emails.map((email) => (
                      <ListItem key={email.id} sx={{ px: 0 }}>
                        <ListItemText primary={email.email} />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleTogglePrimary(email.id, "personal")
                            }
                            disabled={!email.is_active}
                            title={
                              email.is_primary
                                ? "Primary email"
                                : "Set as primary"
                            }
                          >
                            {email.is_primary ? (
                              <StarIcon color="primary" />
                            ) : (
                              <StarBorderIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleToggleActive(
                                email.id,
                                email.is_active,
                                "personal",
                              )
                            }
                            title={
                              email.is_active ? "Disable email" : "Enable email"
                            }
                          >
                            <Switch
                              size="small"
                              checked={Boolean(email.is_active)}
                              disabled={
                                emails.filter((e) => e.is_active).length <= 1 &&
                                email.is_active
                              }
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditEmail(email, "personal")}
                            title="Edit email"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEmail(email.id, "personal")
                            }
                            disabled={emails.length <= 1}
                            title={
                              emails.length <= 1
                                ? "Cannot delete last email"
                                : "Delete email"
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {isAdmin && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Certs &amp; Secrets recipients (global)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddEmail("global")}
                        disabled={
                          !preferences.emailNotifications ||
                          !preferences.globalNotificationsEnabled
                        }
                      >
                        Add Email
                      </Button>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={1}
                    >
                      Org-wide list. Only these addresses receive certificate
                      and secret expiry reminders (not every user&apos;s
                      personal list).
                    </Typography>
                    {globalEmails.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No global recipients configured.
                      </Typography>
                    ) : (
                      <List dense>
                        {globalEmails.map((email) => (
                          <ListItem key={email.id} sx={{ px: 0 }}>
                            <ListItemText primary={email.email} />
                            <ListItemSecondaryAction>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleTogglePrimary(email.id, "global")
                                }
                                disabled={!email.is_active}
                                title={
                                  email.is_primary
                                    ? "Primary email"
                                    : "Set as primary"
                                }
                              >
                                {email.is_primary ? (
                                  <StarIcon color="primary" />
                                ) : (
                                  <StarBorderIcon />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleToggleActive(
                                    email.id,
                                    email.is_active,
                                    "global",
                                  )
                                }
                                title={
                                  email.is_active
                                    ? "Disable email"
                                    : "Enable email"
                                }
                              >
                                <Switch
                                  size="small"
                                  checked={Boolean(email.is_active)}
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEditEmail(email, "global")}
                                title="Edit email"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteEmail(email.id, "global")
                                }
                                title="Delete email"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      label="Compliance inbox (CC on license reminders)"
                      value={complianceCcEmail}
                      onChange={(e) => setComplianceCcEmail(e.target.value)}
                      helperText="Once the Compliance shared inbox exists, set it here. It will be CC'd on all license renewal / expiry emails."
                      placeholder="compliance@example.com"
                      disabled={!preferences.globalNotificationsEnabled}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* License Reminder Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NotificationsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Reminder Timing</Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Used for software license reminders and for certificate/secret
                expiry mail sent to the global recipient list.
              </Typography>

              {/* First Reminder */}
              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>
                  First Reminder: {preferences.firstReminderDays} days before
                  expiry
                </Typography>
                <Slider
                  value={preferences.firstReminderDays}
                  onChange={handleSliderChange("firstReminderDays")}
                  min={1}
                  max={90}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 30, label: "30" },
                    { value: 60, label: "60" },
                    { value: 90, label: "90" },
                  ]}
                  disabled={!preferences.globalNotificationsEnabled}
                />
              </Box>

              {/* Second Reminder */}
              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>
                  Second Reminder: {preferences.secondReminderDays} days before
                  expiry
                </Typography>
                <Slider
                  value={preferences.secondReminderDays}
                  onChange={handleSliderChange("secondReminderDays")}
                  min={1}
                  max={preferences.firstReminderDays - 1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 7, label: "7" },
                    { value: 14, label: "14" },
                    { value: 30, label: "30" },
                  ]}
                  disabled={!preferences.globalNotificationsEnabled}
                />
                {preferences.secondReminderDays >=
                  preferences.firstReminderDays && (
                  <Typography variant="caption" color="error">
                    Second reminder must be before first reminder
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* After Expiry Settings */}
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.dailyReminderAfterExpiry}
                    onChange={(e) =>
                      handleChange("dailyReminderAfterExpiry", e.target.checked)
                    }
                    color="primary"
                    disabled={!preferences.globalNotificationsEnabled}
                  />
                }
                label="Daily Reminders After Expiry"
                sx={{ mb: 2 }}
              />

              {preferences.dailyReminderAfterExpiry && (
                <Box sx={{ mb: 2 }}>
                  <Typography gutterBottom>
                    Stop Daily Reminders After: {preferences.maxDailyReminders}{" "}
                    days
                  </Typography>
                  <Slider
                    value={preferences.maxDailyReminders}
                    onChange={handleSliderChange("maxDailyReminders")}
                    min={1}
                    max={90}
                    marks={[
                      { value: 1, label: "1" },
                      { value: 7, label: "7" },
                      { value: 30, label: "30" },
                      { value: 90, label: "90" },
                    ]}
                    disabled={!preferences.globalNotificationsEnabled}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                You will receive:
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`First reminder ${preferences.firstReminderDays} days before expiry`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label={`Second reminder ${preferences.secondReminderDays} days before expiry`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
                {preferences.dailyReminderAfterExpiry && (
                  <Chip
                    label={`Daily reminders for ${preferences.maxDailyReminders} days after expiry`}
                    size="small"
                    color="warning"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                License reminders will be sent to:
              </Typography>
              <Box sx={{ mt: 1 }}>
                {emails.filter((email) => email.is_active).length === 0 ? (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No active email addresses configured
                  </Typography>
                ) : (
                  emails
                    .filter((email) => email.is_active)
                    .map((email) => (
                      <Chip
                        key={email.id}
                        label={email.email}
                        size="small"
                        color={email.is_primary ? "primary" : "default"}
                        sx={{ mr: 1, mb: 1 }}
                        icon={email.is_primary ? <StarIcon /> : undefined}
                      />
                    ))
                )}
              </Box>
              {isAdmin && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Certs &amp; Secrets reminders will be sent to:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {globalEmails.filter((e) => e.is_active).length === 0 ? (
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No global recipients configured
                      </Typography>
                    ) : (
                      globalEmails
                        .filter((email) => email.is_active)
                        .map((email) => (
                          <Chip
                            key={`g-${email.id}`}
                            label={email.email}
                            size="small"
                            color={email.is_primary ? "primary" : "default"}
                            sx={{ mr: 1, mb: 1 }}
                            icon={email.is_primary ? <StarIcon /> : undefined}
                          />
                        ))
                    )}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Attestation: Schedule Configuration (left) + Reminder Settings (right) */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ScheduleIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Attestation Schedule Configuration
                </Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Configure how often attestations are sent to asset owners for
                review.
              </Typography>

              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Next Scheduled:{" "}
                  {attestationConfig.nextScheduledDate
                    ? formatUkDate(attestationConfig.nextScheduledDate)
                    : "—"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Last Sent:{" "}
                  {attestationConfig.lastSentDate
                    ? formatUkDate(attestationConfig.lastSentDate)
                    : "—"}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={attestationConfig.enabled}
                    onChange={(e) =>
                      setAttestationConfig({
                        ...attestationConfig,
                        enabled: e.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Enable Scheduled Attestations"
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Schedule Frequency</Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Button
                    variant={
                      attestationConfig.scheduleType === "quarterly"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      setAttestationConfig({
                        ...attestationConfig,
                        scheduleType: "quarterly",
                        frequencyMonths: 3,
                      })
                    }
                    disabled={!attestationConfig.enabled}
                  >
                    Quarterly (3 months)
                  </Button>
                  <Button
                    variant={
                      attestationConfig.scheduleType === "six_monthly"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      setAttestationConfig({
                        ...attestationConfig,
                        scheduleType: "six_monthly",
                        frequencyMonths: 6,
                      })
                    }
                    disabled={!attestationConfig.enabled}
                  >
                    Six Monthly (6 months)
                  </Button>
                </Box>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={attestationConfig.autoCreateAttestations}
                    onChange={(e) =>
                      setAttestationConfig({
                        ...attestationConfig,
                        autoCreateAttestations: e.target.checked,
                      })
                    }
                    color="primary"
                    disabled={!attestationConfig.enabled}
                  />
                }
                label="Automatically Create Attestations"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={attestationConfig.testMode}
                    onChange={(e) =>
                      setAttestationConfig({
                        ...attestationConfig,
                        testMode: e.target.checked,
                        testUserId: e.target.checked
                          ? attestationConfig.testUserId
                          : null,
                      })
                    }
                    color="primary"
                    disabled={!attestationConfig.enabled}
                  />
                }
                label="Test Mode (Single User Only)"
                sx={{ mb: 2 }}
              />

              {attestationConfig.testMode && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Test User</InputLabel>
                    <Select
                      value={
                        attestationConfig.testUserId &&
                        testUsers.some(
                          (u) => u.id === attestationConfig.testUserId,
                        )
                          ? String(attestationConfig.testUserId)
                          : ""
                      }
                      label="Test User"
                      onChange={(e) =>
                        setAttestationConfig({
                          ...attestationConfig,
                          testUserId: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        })
                      }
                      disabled={!attestationConfig.enabled || loadingTestUsers}
                    >
                      <MenuItem value="">Select a user...</MenuItem>
                      {testUsers.map((user) => (
                        <MenuItem key={user.id} value={String(user.id)}>
                          {user.first_name} {user.last_name} ({user.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    When test mode is enabled, attestations will only be sent to
                    the selected user.
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleTriggerAttestations}
                  disabled={
                    triggeringAttestations ||
                    !attestationConfig.enabled ||
                    (attestationConfig.testMode &&
                      !attestationConfig.testUserId)
                  }
                  startIcon={
                    triggeringAttestations ? (
                      <CircularProgress size={20} />
                    ) : (
                      <EmailIcon />
                    )
                  }
                >
                  {triggeringAttestations
                    ? "Triggering..."
                    : attestationConfig.testMode
                      ? "Trigger Test Attestations"
                      : "Trigger Attestations for All Users"}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAttestationConfig}
                  disabled={
                    attestationConfigLoading || !attestationConfig.enabled
                  }
                  startIcon={
                    attestationConfigLoading ? (
                      <CircularProgress size={20} />
                    ) : null
                  }
                >
                  {attestationConfigLoading
                    ? "Saving..."
                    : "Save Attestation Config"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Attestation Reminder Settings
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Configure reminders until attestation for the period is
                completed (days before period due).
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  First reminder:{" "}
                  {attestationConfig.attestationFirstReminderDays ?? 14} days
                  before period due
                </Typography>
                <Slider
                  value={attestationConfig.attestationFirstReminderDays ?? 14}
                  onChange={(_, value) =>
                    setAttestationConfig({
                      ...attestationConfig,
                      attestationFirstReminderDays: value,
                    })
                  }
                  min={1}
                  max={60}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 14, label: "14" },
                    { value: 30, label: "30" },
                    { value: 60, label: "60" },
                  ]}
                  disabled={!attestationConfig.enabled}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Second reminder:{" "}
                  {attestationConfig.attestationSecondReminderDays ?? 3} days
                  before period due
                </Typography>
                <Slider
                  value={attestationConfig.attestationSecondReminderDays ?? 3}
                  onChange={(_, value) =>
                    setAttestationConfig({
                      ...attestationConfig,
                      attestationSecondReminderDays: value,
                    })
                  }
                  min={1}
                  max={Math.max(
                    1,
                    Math.min(
                      30,
                      (attestationConfig.attestationFirstReminderDays ?? 14) -
                        1,
                    ),
                  )}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 3, label: "3" },
                    { value: 7, label: "7" },
                    { value: 14, label: "14" },
                  ]}
                  disabled={!attestationConfig.enabled}
                />
                {(attestationConfig.attestationSecondReminderDays ?? 3) >=
                  (attestationConfig.attestationFirstReminderDays ?? 14) && (
                  <Typography variant="caption" color="error">
                    Second reminder must be before first reminder
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Email Templates */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Attestation Email Templates</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddTemplate}
            >
              Add Template
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Create and manage email templates for attestation notifications. Use
            placeholders to personalize emails.
          </Typography>

          {emailTemplates.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontStyle: "italic" }}
            >
              No email templates configured. Create one to get started.
            </Typography>
          ) : (
            <List>
              {emailTemplates.map((template) => (
                <ListItem
                  key={template.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={template.template_name}
                    secondary={
                      <Box component="div">
                        <Typography
                          variant="caption"
                          display="block"
                          component="div"
                        >
                          Type: {template.template_type.replace("_", " ")}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          component="div"
                        >
                          Subject: {template.subject}
                        </Typography>
                        <Box component="div" sx={{ mt: 0.5 }}>
                          <Chip
                            label={template.is_active ? "Active" : "Inactive"}
                            size="small"
                            color={template.is_active ? "success" : "default"}
                          />
                        </Box>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleSuspendTemplate(template)}
                      title={
                        template.is_active
                          ? "Suspend template"
                          : "Activate template"
                      }
                      color={template.is_active ? "default" : "success"}
                    >
                      {template.is_active ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                    <IconButton
                      onClick={() => handleEditTemplate(template)}
                      title="Edit template"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteTemplate(template)}
                      title="Delete template"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !preferences.globalNotificationsEnabled}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? "Saving..." : "Save Preferences"}
        </Button>
      </Box>

      {/* Email Management Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingEmail
            ? emailListType === "global"
              ? "Edit Global Recipient"
              : "Edit Email Address"
            : emailListType === "global"
              ? "Add Global Recipient"
              : "Add Email Address"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            placeholder="Enter email address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEmail} variant="contained">
            {editingEmail ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? "Edit Email Template" : "Create Email Template"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Template Name"
                fullWidth
                value={templateForm.templateName}
                onChange={(e) => {
                  setTemplateForm({
                    ...templateForm,
                    templateName: e.target.value,
                  });
                  if (templateErrors.templateName) {
                    setTemplateErrors({ ...templateErrors, templateName: "" });
                  }
                }}
                error={!!templateErrors.templateName}
                helperText={templateErrors.templateName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Template Type"
                fullWidth
                SelectProps={{ native: true }}
                value={templateForm.templateType}
                onChange={(e) =>
                  setTemplateForm({
                    ...templateForm,
                    templateType: e.target.value,
                  })
                }
                required
              >
                <option value="quarterly_review">Quarterly Review</option>
                <option value="device_receipt">Device Receipt</option>
                <option value="recertification">Recertification</option>
                <option value="contractor_byod">Contractor BYOD</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Subject"
                fullWidth
                value={templateForm.subject}
                onChange={(e) => {
                  setTemplateForm({ ...templateForm, subject: e.target.value });
                  if (templateErrors.subject) {
                    setTemplateErrors({ ...templateErrors, subject: "" });
                  }
                }}
                error={!!templateErrors.subject}
                helperText={templateErrors.subject}
                placeholder="e.g., Quarterly Asset Review - {{review_period}}"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Email Body (Rich Text) <span style={{ color: "red" }}>*</span>
              </Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: templateErrors.bodyHtml
                    ? "error.main"
                    : "divider",
                  borderRadius: 1,
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={templateForm.bodyHtml}
                  onChange={(value) => {
                    setTemplateForm({ ...templateForm, bodyHtml: value });
                    if (templateErrors.bodyHtml) {
                      setTemplateErrors({ ...templateErrors, bodyHtml: "" });
                    }
                  }}
                  style={{ minHeight: "200px" }}
                />
              </Box>
              {templateErrors.bodyHtml && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {templateErrors.bodyHtml}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Available Placeholders:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {availablePlaceholders.map((item) => (
                  <Chip
                    key={item.placeholder}
                    label={item.placeholder}
                    size="small"
                    title={item.description}
                    onClick={() => {
                      const quill = quillRef.current?.getEditor?.();
                      insertQuillPlaceholder(quill, item.placeholder);
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {error && (
            <Alert severity="error" sx={{ flex: 1, mr: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            onClick={() => {
              setTemplateDialogOpen(false);
              setTemplateErrors({});
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveTemplate} variant="contained">
            {editingTemplate ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationPreferences;
